/**
 * Google sign-in is a plain OAuth authorisation-code redirect, not the Firebase
 * popup SDK: the browser leaves for accounts.google.com, comes back to
 * `/auth/google?code=…`, and the backend swaps that code for a session
 * (`GET /google/user/profile/`). Same flow for signing in and signing up — the
 * backend creates the account if the Google address is new — which is why both
 * auth pages share one button handler.
 */
const CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '402277231112-imkppquf33eo2ap9btus3lr7m74okdvh.apps.googleusercontent.com';

/** Registered with Google as a redirect URI — changing it breaks the consent step. */
export const GOOGLE_CALLBACK_PATH = '/auth/google';

/** What the old app sends when there's nowhere particular to return to. */
const NO_NEXT_STATE = 'pass-through_value';

/**
 * Google only redirects back to URIs registered against the client ID. Live,
 * this app is served from the domain the old dashboard ran on, which already
 * has `<origin>/auth/google` registered — the old app sent its own origin too.
 * `origin` never ends in a slash, so the path joins cleanly.
 */
const redirectOrigin = () => window.location.origin;

/**
 * Google matches this string exactly against the client's registered list and
 * answers `redirect_uri_mismatch` on any difference — a port, a trailing slash,
 * http vs https. The override exists so an environment whose registered URI
 * isn't `<origin>/auth/google` is a `.env.local` line rather than a code edit.
 */
export const googleRedirectUri = () =>
  process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${redirectOrigin()}${GOOGLE_CALLBACK_PATH}`;

/**
 * `next` is where the trader was heading before being sent to sign in. It rides
 * along in OAuth's `state`, which Google echoes back untouched.
 */
export const googleAuthUrl = (next) => {
  if (process.env.NODE_ENV === 'development') {
    // The string to paste into the Google console when the consent screen
    // answers `redirect_uri_mismatch`.
    console.info('[google-auth] redirect_uri:', googleRedirectUri());
  }

  const params = new URLSearchParams({
    scope: 'https://www.googleapis.com/auth/userinfo.email',
    include_granted_scopes: 'true',
    response_type: 'code',
    // Encoded here as well as by URLSearchParams: the callback decodes one
    // layer, matching how the old app writes and reads this value.
    state: next ? encodeURIComponent(next) : NO_NEXT_STATE,
    redirect_uri: googleRedirectUri(),
    client_id: CLIENT_ID,
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
};

/**
 * "Remember for 30 days" has to survive the trip to accounts.google.com — the
 * trader ticks it on the sign-in form, and the session it applies to is only
 * created once the browser comes back.
 *
 * Deliberately not in OAuth `state`: that returns through a URL anyone could
 * have written, which is why `nextFromOAuthState` below treats it as untrusted.
 * sessionStorage is same-tab, same-origin and gone when the tab closes — the
 * same lifetime as the choice itself.
 */
const REMEMBER_KEY = 'auth.google.remember';

export const rememberForGoogle = (remember) => {
  try {
    if (remember) sessionStorage.setItem(REMEMBER_KEY, '1');
    else sessionStorage.removeItem(REMEMBER_KEY);
  } catch {
    // Storage blocked (private mode, a locked-down browser): the sign-in still
    // works, the session just isn't renewed.
  }
};

/** Reads the flag and clears it — one redirect, one answer. */
export const takeGoogleRemember = () => {
  try {
    const remembered = sessionStorage.getItem(REMEMBER_KEY) === '1';
    sessionStorage.removeItem(REMEMBER_KEY);
    return remembered;
  } catch {
    return false;
  }
};

/**
 * The `state` we get back is attacker-writable — it survives a round trip
 * through a URL — so only in-app paths are honoured. `//host` is rejected
 * along with absolute URLs: browsers read it as protocol-relative.
 */
export const nextFromOAuthState = (state) => {
  if (!state || state === NO_NEXT_STATE) return null;

  let path;
  try {
    path = decodeURIComponent(state);
  } catch {
    return null;
  }

  return path.startsWith('/') && !path.startsWith('//') ? path : null;
};
